<template>
  <div class="pay-container">
    <div v-if="isLoading" class="google-pay-loader"></div>
    <google-pay-button
      v-if="paymentRequest"
      ref="googlePayBtn"
      :environment="environment"
      button-type="plain"
      button-color="black"
      :paymentRequest="paymentRequest"
      @loadpaymentdata="onLoadPaymentData"
      @error="onError"
    />
  </div>
</template>

<script>
import '@google-pay/button-element';
import braintree from 'braintree-web';

export default {
  name: 'GooglePay',
  props: {
    currentSelectedPlan: {
      type: Object,
      default: () => ({ price: 0 })
    },
    sendNonce: {
      type: Function,
      default: () => {}
    },
    clientToken: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      googlePayInstance: null,
      paymentRequest: null,
      isLoading: true,
      environment: process.env.VUE_APP_BRAINTREE_ENV.toUpperCase(),
    };
  },
  watch: {
    // Watch for changes in clientToken
    async clientToken(newToken) {
      if (newToken) {
        await this.initializeGooglePay(newToken);
      }
    }
  },
  methods: {
    async initializeGooglePay(token) {
      this.isLoading = true;
      console.log('Environment: ', this.environment);
      try {
        const clientInstance = await braintree.client.create({
          authorization: token
        });

        const googlePaymentInstance = await braintree.googlePayment.create({
          client: clientInstance,
          googlePayVersion: 2,
          // Google pay MID
          googleMerchantId: this.environment === 'PRODUCTION' ? process.env.VUE_APP_GOOGLEPAY_MERCHANT_ID : undefined
        });

        const paymentsClient = new google.payments.api.PaymentsClient({
          environment: this.environment,
        });

        const isReady = await paymentsClient.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: googlePaymentInstance.createPaymentDataRequest().allowedPaymentMethods
        });

        if (!isReady.result) {
          console.warn('Google Pay is not available');
          this.isLoading = false;
          return;
        }

        const dataCollectorInstance = await braintree.dataCollector.create({
          client: clientInstance,
          kount: true // Required for deviceData
        });

        this.deviceData = dataCollectorInstance.deviceData;

        const paymentRequest = googlePaymentInstance.createPaymentDataRequest({
          transactionInfo: {
            currencyCode: 'USD',
            totalPriceStatus: 'FINAL',
            totalPrice: this.calculateTotalPrice()
          },
          environment: this.environment !== 'PRODUCTION' ? "TEST" : this.environment,
          merchantInfo: {
            merchantName: process.env.VUE_APP_GOOGLEPAY_MERCHANT_NAME,
            ...(this.environment === 'PRODUCTION' && { merchantId: process.env.VUE_APP_GOOGLEPAY_MERCHANT_ID })
          },
          shippingAddressRequired: false
        });

        // Add required card networks & auth methods
        const cardMethod = paymentRequest.allowedPaymentMethods[0];
        cardMethod.parameters.allowedCardNetworks = ['VISA', 'MASTERCARD', 'AMEX'];
        cardMethod.parameters.allowedAuthMethods = ['PAN_ONLY', 'CRYPTOGRAM_3DS'];
        // cardMethod.parameters.billingAddressRequired = true;
        cardMethod.parameters.billingAddressParameters = {
          format: 'FULL',
          // phoneNumberRequired: true
        };

        // Set the paymentRequest in the component's data
        this.paymentRequest = paymentRequest;

        // Now, set it to the google-pay-button element after it's rendered
        this.$nextTick(() => {
          if (this.$refs.googlePayBtn) {
            this.$refs.googlePayBtn.paymentRequest = paymentRequest;
          }
        });
        this.googlePayInstance = googlePaymentInstance;

      } catch (err) {
        console.error('Error initializing Google Pay:', err);
      } finally {
        this.isLoading = false;
      }
    },

    async onLoadPaymentData(event) {
      try {
        const payload = await this.googlePayInstance.parseResponse(event.detail);

        const nonce = payload.nonce;

        this.sendNonce(nonce, '', this.deviceData);
      } catch (error) {
        console.error('Error processing Google Pay data:', error);
      }
    },

    onError(event) {
      console.error('Google Pay Error:', event.detail);
    },

    calculateTotalPrice() {
      const price = this.currentSelectedPlan?.price || 0;
      return price.toFixed(2);
    }
  }
};
</script>

<style scoped>
.pay-container {
  display: flex;
  width: 100%;
  justify-content: center;
}

.google-pay-loader {
  width: 20px;
  height: 20px;
  margin: 10px;
  border: 2px solid #020076;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

google-pay-button {
  width: 100%;
}

::v-deep google-pay-button div {
  display: flex;
}

::v-deep .gpay-card-info-container {
  width: 100%;
  height: 42px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}

::v-deep iframe {
  margin-top: 4px;
  transform: scale(1.5);
}

::v-deep button:focus {
  outline: none !important;
}
</style>